import type { PermissionsMap } from "../auth/types";

export type { PermissionsMap };

export function normalizePermissions(raw: unknown): PermissionsMap {
  if (!raw) {
    return {};
  }

  if (Array.isArray(raw)) {
    return {};
  }

  if (typeof raw !== "object") {
    return {};
  }

  const out: PermissionsMap = {};
  for (const [orgId, roles] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(roles)) {
      const cleaned = roles.map(String).filter(Boolean);
      if (cleaned.length > 0) {
        out[String(orgId)] = cleaned;
      }
    }
  }
  return out;
}

export function isFlatPermissions(raw: unknown): raw is string[] {
  return Array.isArray(raw);
}

type UserWithLegacyPermissions =
  | { data?: { permissions_legacy?: unknown; permissions?: unknown } | null }
  | null
  | undefined;

/**
 * Legacy org -> role-name map for a user. `data.permissions` is now always
 * the flat granular-permission-string array; the dict of legacy role names
 * moved to `data.permissions_legacy`. Falls back to `permissions` for any
 * user still carrying the dict shape there (pre-migration).
 */
export function getLegacyRoleMap(user: UserWithLegacyPermissions): PermissionsMap {
  if (!user?.data) {
    return {};
  }
  return normalizePermissions(user.data.permissions_legacy ?? user.data.permissions);
}

/** True if the user holds any of the given legacy role names, in any org. */
export function userHasAnyLegacyRole(
  user: UserWithLegacyPermissions,
  roleNames: readonly string[],
): boolean {
  const map = getLegacyRoleMap(user);
  return Object.values(map).some((roles) => roles.some((role) => roleNames.includes(role)));
}

export function mergePermissions(a: PermissionsMap, b: PermissionsMap): PermissionsMap {
  const merged: PermissionsMap = { ...a };
  for (const [orgId, roles] of Object.entries(b)) {
    const existing = new Set((merged[orgId] || []).map(String));
    roles.forEach((r) => existing.add(String(r)));
    merged[orgId] = Array.from(existing);
  }
  return merged;
}

export function formatPermissionsForDisplay(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map(String);
  }
  if (raw && typeof raw === "object") {
    return Object.entries(raw as PermissionsMap).flatMap(([orgId, roles]) =>
      roles.map((r) => `${orgId}: ${r}`),
    );
  }
  return [];
}
